import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useUsers, useDeleteUser, useToggleUserStatus } from '../../../hooks/useUsers';
import { USER_STATUS, USER_ROLES } from '../../../utils/constants';
import { useToast } from '../../../hooks/useToast';
import UsersTable from '../../../components/tables/UsersTable';
import Loading from '../../../components/common/Loading';
import ErrorMessage from '../../../components/common/ErrorMessage';
import Button from '../../../components/common/Button';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import Pagination from '../../../components/common/Pagination';
import { Plus, Search, Filter } from 'lucide-react';

const UsersList = () => {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    role: '',
  });

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    id: null,
  });

  const { data, isLoading, error, refetch } = useUsers({ ...filters, page });
  const deleteUser = useDeleteUser();
  const toggleStatus = useToggleUserStatus();
  const toast = useToast();

  const handleFilterChange = (field, value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setPage(1);
    setFilters({
      search: '',
      status: '',
      role: '',
    });
  };

  const handleDelete = (id) => {
    setConfirmDialog({ isOpen: true, id });
  };

  const handleConfirmDelete = async () => {
    const { id } = confirmDialog;
    setConfirmDialog({ isOpen: false, id: null });

    try {
      await deleteUser.mutateAsync(id);
      toast.success('Usuário excluído com sucesso!');
    } catch (err) {
      toast.error('Erro ao excluir usuário: ' + err.message);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await toggleStatus.mutateAsync(id);
      toast.success('Status alterado com sucesso!');
    } catch (err) {
      toast.error('Erro ao alterar status: ' + err.message);
    }
  };

  if (isLoading) {
    return <Loading text="Carregando usuários..." />;
  }

  if (error) {
    return <ErrorMessage message={error.message} onRetry={refetch} />;
  }

  const users = data?.data || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usuários</h1>
          <p className="text-gray-600 mt-1">
            Gerencie os usuários e associados do sistema
          </p>
        </div>
        <Link to="/admin/usuarios/novo">
          <Button variant="primary">
            <Plus className="h-5 w-5 mr-2" />
            Novo Usuário
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <Filter className="h-5 w-5 text-gray-600 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">Filtros</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="label">Buscar</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Nome, email ou CPF..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="input pl-10"
              />
            </div>
          </div>

          <div>
            <label className="label">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="input"
            >
              <option value="">Todos</option>
              <option value={USER_STATUS.ACTIVE}>Ativo</option>
              <option value={USER_STATUS.INACTIVE}>Inativo</option>
              <option value={USER_STATUS.PENDING}>Pendente</option>
            </select>
          </div>

          <div>
            <label className="label">Função</label>
            <select
              value={filters.role}
              onChange={(e) => handleFilterChange('role', e.target.value)}
              className="input"
            >
              <option value="">Todas</option>
              <option value={USER_ROLES.ADMIN}>Administrador</option>
              <option value={USER_ROLES.USER}>Usuário</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={clearFilters}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Limpar filtros
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm text-gray-600 mb-1">Total de Usuários</div>
          <div className="text-3xl font-bold text-gray-900">{users.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm text-gray-600 mb-1">Usuários Ativos</div>
          <div className="text-3xl font-bold text-green-600">
            {users.filter((u) => u.status === 'active').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm text-gray-600 mb-1">Pendentes</div>
          <div className="text-3xl font-bold text-yellow-600">
            {users.filter((u) => u.status === 'pending').length}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <UsersTable
          users={users}
          onDelete={handleDelete}
          onToggleStatus={handleToggleStatus}
        />
        <Pagination
          page={data?.page || 1}
          pageSize={data?.pageSize || 15}
          total={data?.total || 0}
          onPageChange={setPage}
        />
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, id: null })}
        onConfirm={handleConfirmDelete}
        title="Excluir usuário"
        message="Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        variant="destructive"
      />
    </div>
  );
};

export default UsersList;
